import { useCallback, useEffect, useMemo, useState } from "react";
import {
  EuiFieldSearch,
  EuiFilterButton,
  EuiIcon,
  EuiPopoverTitle,
  useGeneratedHtmlId,
  EuiCheckbox,
  EuiFlexGroup,
  EuiFlexItem,
  EuiSelectable,
} from "@elastic/eui";
import { useDropdownStyles } from "../FDFilterDropdownStyles";
import { useDispatch, useSelector } from "react-redux";
import Emitter from "services/UtilsService/Emitter";
import { isNullOrUndefined, isSubjectAuthorizedForUser } from "utils";
import { NEGATE_FILTER, PERFMON_SPLIT_KEY } from "Consts";
import globalFilterTypes from "reduxStore/globalFilter/globalFilter.types";
import { globalFilterSelector } from "reduxStore/globalFilter/globalFilter.selectors";
import { getDropdownData } from "../FDFilterDropdownService";
import Popover from "components/Popover/Popover";
import IconButton from "components/Buttons/IconButton";
import CustomSelector from "../performance/CustomSelector";
import ViewTemplateSelector from "../components/ViewTemplateSelector";
import FDFilterDropdownFooter from "components/FDFilterDropdown/components/FDFilterDropdownFooter";
import { Roles, Subjects } from "settings/permissions";
import "styles/components/filterDropdown/fd_filter_dropdown.css";

const FDFilterDropdownPerformance = ({
  FdType,
  Items = [],
  Name = "",
  Field = "",
  OnCreate = null,
  OnClick = null,
  OnApply = null,
  OnEdit = null,
  MaxSelectedItems = null,
  OnMaxSelection = null,
  SingleSelection = false,
  AllowExclude = true,
  IsItemVisibleFunc = null,
  ForceSelectedValue = null,
  ID = null,
  popoverButtonProps = {},
  perfmonFilterId,
  isPerformaceFilter = false,
  isDisabled = false,
  actionButtonEnabled,
  isHoverEnabled = false,
}: any) => {
  const classes = useDropdownStyles();
  const dispatch = useDispatch();
  const [filterText, setFilterText] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [isPopoverOpen, setIsPopoverOpen] = useState<boolean>(false);
  const [isSelectAll, setSelectAll] = useState<boolean>(false);
  const [items, setItems] = useState(Items || []);
  const [activeFilters, setActiveFilters] = useState<any>({});
  const [applyItem, setApplyItem] = useState({});
  const [applyItems, setApplyItems] = useState<any>([]);
  const isTemplate = Field === "template";
  const isDashboard = Field === "dashboard";

  const {
    clientId,
    GlobalTopFilterObject,
    DatePickerParams,
    PerformanceFilterObject,
    PerformanceTemplateObject,
    PerformanceDashboardObject,
  } = useSelector(globalFilterSelector);

  const setTemplateObject = (template: any) => {
    dispatch({
      payload: {
        key: perfmonFilterId,
        [perfmonFilterId]: template,
      },
      type: globalFilterTypes.UPDATE_PERFORMANCE_TEMPLATE,
    });
  };

  const setDashboardObject = (payload: any) => {
    dispatch({
      payload,
      type: globalFilterTypes.UPDATE_PERFORMANCE_DASHBOARD,
    });
  };

  const perfMonFilterObject =
    !isNullOrUndefined(PerformanceFilterObject[perfmonFilterId]) &&
    Object.keys(PerformanceFilterObject)?.length > 0
      ? PerformanceFilterObject[perfmonFilterId]
      : {};

  const canCreateUpdateGlobalTemplates = isSubjectAuthorizedForUser({
    subject: Subjects.Performance_Counter_Template_Management,
    role: Roles.Admin,
  });
  const canCreateUpdatePrivateTemplates = isSubjectAuthorizedForUser({
    subject: Subjects.Performance_Counter_Template_Management,
    role: Roles.Write,
  });

  if (isDashboard) {
    return (
      <ViewTemplateSelector
        isDashboard
        Items={[
          {
            id: 1,
            display: "My Dashboards",
            name: "My Dashboards",
            value: "My Dashboards",
            type: "local",
            values: Items.filter(
              (i: any) => !i?.attributes?.isGlobal && !i?.attributes?.isSystem
            ).map((i: any) => ({
              ...i,
              name: i.type,
              value: i.name,
            })),
          },
          {
            id: 2,
            display: "Global Dashboards",
            name: "Global Dashboards",
            value: "Global Dashboards",
            type: "global",
            values: Items.filter(
              (i: any) => i?.attributes?.isGlobal && !i?.attributes?.isSystem
            ).map((i: any) => ({
              ...i,
              name: i.type,
              value: i.name,
            })),
          },
          {
            id: 3,
            display: "WISdom Dashboards",
            name: "WISdom Dashboards",
            value: "WISdom Dashboards",
            type: "system",
            values: Items.filter((i: any) => i?.attributes?.isSystem).map((i: any) => ({
              ...i,
              name: i.type,
              value: i.name,
            })),
          },
        ]}
        Name={"Dashboards"}
        Field={"dashboard"}
        perfmonFilterId={perfmonFilterId}
        filterObject={
          !isNullOrUndefined(PerformanceDashboardObject) && PerformanceDashboardObject?.length > 0
            ? PerformanceDashboardObject
            : []
        }
        setFilterObject={setDashboardObject}
        anchorPosition="downLeft"
        singleSelection={true}
        disableSorting
        onCreate={OnCreate}
        onApply={OnApply}
        onEdit={OnEdit}
        onCancel={() => {}}
      />
    );
  }

  if (isTemplate) {
    return (
      <ViewTemplateSelector
        Items={[
          {
            id: 1,
            display: "My Templates",
            name: "My Templates",
            value: "My Templates",
            type: "local",
            values: Items.filter(
              (i: any) => !i?.attributes?.isGlobal && !i?.attributes?.isSystem
            ).map((i: any) => ({
              ...i,
              name: i.type,
              value: i.name,
            })),
          },
          {
            id: 2,
            display: "Global Templates",
            name: "Global Templates",
            value: "Global Templates",
            type: "global",
            values: Items.filter(
              (i: any) => i?.attributes?.isGlobal && !i?.attributes?.isSystem
            ).map((i: any) => ({
              ...i,
              name: i.type,
              value: i.name,
            })),
          },
          {
            id: 3,
            display: "WISdom Templates",
            name: "WISdom Templates",
            value: "WISdom Templates",
            type: "system",
            values: Items.filter((i: any) => i?.attributes?.isSystem).map((i: any) => ({
              ...i,
              name: i.type,
              value: i.name,
            })),
          },
        ]}
        Name={"Templates"}
        Field={"template"}
        perfmonFilterId={perfmonFilterId}
        filterObject={
          !isNullOrUndefined(PerformanceTemplateObject) &&
          Object.keys(PerformanceTemplateObject)?.length > 0
            ? PerformanceTemplateObject[perfmonFilterId]
            : []
        }
        setFilterObject={setTemplateObject}
        anchorPosition="downLeft"
        singleSelection={false}
        disableSorting
        onCreate={OnCreate}
        onApply={OnApply}
        onEdit={OnEdit}
        onCancel={() => {
          // setTemplateObject([]);
        }}
        actionButtonEnabled={actionButtonEnabled}
        canCreateUpdateGlobalPerfMonTemplates={canCreateUpdateGlobalTemplates}
        canCreateUpdatePrivatePerfMonTemplates={canCreateUpdatePrivateTemplates}
      />
    );
  }

  if (Name?.includes("Resources")) {
    return (
      <CustomSelector
        Items={Items}
        Name={"Resources"}
        Field={"servername"}
        isHoverEnabled={isHoverEnabled}
        filterObject={perfMonFilterObject["servername"] || []}
        setFilterObject={(list: any) => {
          let resourceType = "";
          dispatch({
            payload: {
              key: perfmonFilterId,
              [perfmonFilterId]: {
                [Field]: list?.map((i: any) => {
                  const [key, value] = i.split(PERFMON_SPLIT_KEY);
                  resourceType = key;
                  return i;
                }),
                resourceType,
              },
            },
            type: globalFilterTypes.UPDATE_PERFORMANCE_FILTER,
          });
        }}
        anchorPosition="leftCenter"
      />
    );
  }

  if (Name?.includes("Category")) {
    return (
      <CustomSelector
        Items={Items}
        Name={"Category"}
        Field={"category"}
        // singleTopLevelSelection={false}
        filterObject={perfMonFilterObject["category"] || []}
        setFilterObject={(list: any) => {
          dispatch({
            payload: {
              key: perfmonFilterId,
              [perfmonFilterId]: {
                [Field]: list?.map((i: any) => {
                  return i;
                }),
              },
            },
            type: globalFilterTypes.UPDATE_PERFORMANCE_FILTER,
          });
        }}
        anchorPosition="leftCenter"
      />
    );
  }

  const getSelectedFilters = () => {
    const filterArr = perfMonFilterObject[Field];
    if (
      SingleSelection &&
      (filterArr?.length > 1 ||
        (filterArr?.length === 1 && filterArr[0]?.startsWith(NEGATE_FILTER)))
    ) {
      return [];
    } else return perfMonFilterObject[Field];
  };

  const currentFilterValue = getSelectedFilters();

  useEffect(() => {
    //listen for reset events from outside
    const onResetEventKey = `FDFilterDropdown_onreset_${ID}`;
    if (ID) {
      Emitter.on(onResetEventKey, () => {
        resetSelected();
      });
    }
    return () => {
      if (ID) {
        //@ts-ignore
        Emitter.off(onResetEventKey);
      }
    };
  }, [ID]);

  const onSingleSelectionClick = (selectedItem: any) => {
    const newFilterStatusList = { [selectedItem.name]: "on" };
    if (activeFilters[selectedItem.name] == "on") {
      delete newFilterStatusList[selectedItem.name];
    }
    setActiveFilters(newFilterStatusList);

    if (OnClick) {
      //@ts-ignore
      OnClick({
        display: selectedItem.display,
        list: newFilterStatusList,
        name: selectedItem.name,
        selectedItem,
        value: "on",
      });
    }

    if (OnApply) {
      setApplyItem(selectedItem);
    }
  };

  const publishGlobalFilter = useCallback(
    (values: any) => {
      const newFieldGlobalFilterValues = AllowExclude
        ? Object.entries(values).map(([listItem, value]) => {
            if (value === "off") {
              return `${NEGATE_FILTER}${listItem}`;
            }
            return listItem;
          })
        : Object.entries(values)
            .filter(([_, value]) => value !== "off")
            .map(([listItem, _]) => listItem);

      if (!AllowExclude) setActiveFilters({});

      dispatch({
        payload: {
          key: perfmonFilterId,
          [perfmonFilterId]: {
            [Field]: newFieldGlobalFilterValues.length > 0 ? newFieldGlobalFilterValues : null,
          },
        },
        type: globalFilterTypes.UPDATE_PERFORMANCE_FILTER,
      });
    },
    [Field, isPerformaceFilter]
  );

  const isLimitReached = (currentChecked: any) => {
    if (MaxSelectedItems && currentChecked === undefined) {
      const excludedPlusIncludedItems = Object.values(activeFilters).filter((v) => v)?.length;

      if (excludedPlusIncludedItems >= MaxSelectedItems && OnMaxSelection) {
        //@ts-ignore
        OnMaxSelection();
      }
      return excludedPlusIncludedItems >= MaxSelectedItems;
    }
    return false;
  };

  useEffect(() => {
    if (isSelectAll && items?.length !== perfMonFilterObject[Field]?.length) {
      setSelectAll(false);
    }
  }, [perfMonFilterObject]);

  useEffect(() => {
    getDropdownData({
      DatePickerParams,
      GlobalTopFilterObject,
      clientId,
      fdType: FdType,
      setItems,
      setLoading,
    });
  }, [FdType, clientId]);

  useEffect(() => {
    if (Items) {
      // list of options came from outside of the component
      if (Items != null) {
        const cleanItems = Items.filter(
          (option: any) => option.value !== undefined && option.value !== null
        );

        setItems(
          cleanItems.map((f: any) => ({
            ...f,
            name: f.value, // todo: why? maybe check if doesnt have name already??
          }))
        );
      }
    }
  }, [JSON.stringify(Items)]);

  useEffect(() => {
    const map: any = {};

    if (currentFilterValue) {
      let valueOutOfRangeExists = false;
      for (const filterVal of currentFilterValue) {
        // prevent selected value where it's not ont the given filter list
        if (
          typeof filterVal == "string" &&
          items.some((item: any) => item.name == filterVal?.replace(NEGATE_FILTER, ""))
        ) {
          // turning into map for faster searches later
          if (filterVal?.startsWith(NEGATE_FILTER)) {
            map[filterVal.replace(NEGATE_FILTER, "")] = "off";
          } else {
            map[filterVal] = "on";
          }
        } else {
          valueOutOfRangeExists = true;
        }
      }

      setActiveFilters(map);

      if (items.length && valueOutOfRangeExists) {
        publishGlobalFilter(map);
      }
    } else {
      resetSelected();
    }
  }, [currentFilterValue, isPerformaceFilter, JSON.stringify(items)]);

  const togglePopover = () => {
    setIsPopoverOpen(!isPopoverOpen);
  };

  const closePopover = () => {
    setIsPopoverOpen(false);
  };

  const visibleItems = useMemo(() => {
    const result = IsItemVisibleFunc
      ? //@ts-ignore
        items.filter((item: any) => IsItemVisibleFunc(item.display ?? item.name))
      : items;

    const selected = result.filter((i: any) => perfMonFilterObject[Field]?.includes(i.name));

    const excluded = result.filter((i: any) =>
      perfMonFilterObject[Field]?.includes(`${NEGATE_FILTER}${i.name}`)
    );

    const nSelected = result
      .filter(
        (i: any) =>
          !perfMonFilterObject[Field]?.includes(i.name) &&
          !perfMonFilterObject[Field]?.includes(`${NEGATE_FILTER}${i.name}`)
      )
      .sort((a: any, b: any) => {
        return a.display ? a.display.localeCompare(b.display) : a.name?.localeCompare(b.name);
      });

    return [...selected, ...excluded, ...nSelected];
  }, [IsItemVisibleFunc, items, activeFilters]);

  const resetSelected = useCallback(() => {
    if (
      !isNullOrUndefined(ForceSelectedValue) &&
      items.length > 0 &&
      !isNullOrUndefined(perfMonFilterObject)
    ) {
      const arrSelected = Array.isArray(ForceSelectedValue)
        ? ForceSelectedValue
        : [ForceSelectedValue];

      const filterListStatusCopy = SingleSelection ? {} : { ...activeFilters };
      arrSelected.forEach((selected) => {
        const selectedItemIndex = items.findIndex((category: any) => category.value == selected);
        if (selectedItemIndex > -1) {
          const selectedItem: any = items[selectedItemIndex];

          if (selectedItem && selectedItem.name) {
            filterListStatusCopy[selectedItem.name] = "on";
          }
        }
      });

      setActiveFilters(filterListStatusCopy);
      publishGlobalFilter(filterListStatusCopy);
    } else {
      setActiveFilters({});
      publishGlobalFilter({});
    }
  }, [ForceSelectedValue, Name, JSON.stringify(activeFilters), JSON.stringify(items)]);

  useEffect(() => {
    if (
      !isNullOrUndefined(ForceSelectedValue) &&
      items.length > 0 &&
      !isNullOrUndefined(perfMonFilterObject)
    ) {
      const arrSelected = Array.isArray(ForceSelectedValue)
        ? ForceSelectedValue
        : [ForceSelectedValue];

      const filterListStatusCopy = SingleSelection ? {} : { ...activeFilters };
      arrSelected.forEach((selected) => {
        const selectedItemIndex = items.findIndex((category: any) => category.value == selected);
        if (selectedItemIndex > -1) {
          const selectedItem: any = items[selectedItemIndex];

          if (selectedItem && selectedItem.name) {
            filterListStatusCopy[selectedItem.name] = "on";
          }
        }
      });
      setActiveFilters(filterListStatusCopy);
      publishGlobalFilter(filterListStatusCopy);
    }
  }, []);

  useEffect(
    () => () => {
      setItems([]);
      setLoading(false);
    },
    []
  );

  const getIcon = (field: string) => {
    if (field === "template") return "layers";
    if (field === "tag") return "tag";
    return "filter";
  };

  const activeFiltersCount = Object.keys(activeFilters).length;
  const filterArrowDownButton = (
    <EuiFilterButton
      className={activeFiltersCount == 0 ? "euiFilterButton__notification_hidden" : ""}
      data-test-subj={`filter-dd-${Name}`}
      hasActiveFilters={activeFiltersCount > 0}
      iconType="arrowDown"
      iconSize="s"
      disabled={isDisabled}
      isSelected={isPopoverOpen}
      numActiveFilters={activeFiltersCount}
      numFilters={visibleItems.length}
      onClick={togglePopover}
      style={{
        // background: "white",
        border: "1px solid var(--border-color)",
        borderRadius: "4px",
        boxShadow: "none",
        height: "34px",
        marginRight: "10px",
        position: "relative",
      }}
      {...popoverButtonProps}>
      <span style={{ position: "absolute", top: "5px" }}>
        <EuiIcon type={getIcon(Field)} />
      </span>
      <span style={{ paddingLeft: "20px" }}>{Name}</span>
    </EuiFilterButton>
  );

  const filterItemClick = (item: any) => {
    setSelectAll(false);
    const currentChecked = activeFilters[item.name];
    let newChecked;

    if (AllowExclude) {
      if (currentChecked !== undefined) {
        if (currentChecked === "on") {
          newChecked = "off";
        } else if (currentChecked === "off" || currentChecked === "on") {
          newChecked = undefined;
        }
      } else {
        newChecked = "on";
      }
    }

    if (!AllowExclude) {
      newChecked = currentChecked === "on" ? undefined : "on";
    }

    if (SingleSelection) {
      onSingleSelectionClick(item);
      return;
    } else {
      if (isLimitReached(currentChecked)) {
        return;
      }
    }

    const filterListStatusCopy = { ...activeFilters };

    if (newChecked === undefined) {
      delete filterListStatusCopy[item.name];
    } else {
      filterListStatusCopy[item.name] = newChecked;
    }

    setActiveFilters(filterListStatusCopy);

    if (!SingleSelection && OnApply) {
      setApplyItems(Object?.keys(filterListStatusCopy));
    }

    if (OnClick) {
      //@ts-ignore
      OnClick({
        list: filterListStatusCopy,
        name: item.name,
        selectedItem: item,
        value: currentChecked === "on" ? "off" : "on",
      });
    }
  };

  const handleSelectAll = () => {
    const filterListStatusCopy: any = {};
    items
      .filter((item: any) => {
        return (
          filterText.length === 0 ||
          (item.name + "").toLowerCase().includes(filterText.toLowerCase()) ||
          (item?.display ?? "").toLowerCase().includes(filterText.toLowerCase())
        );
      })
      .map((i: any) => i.name)
      .forEach((key: any) => {
        if (!isSelectAll) filterListStatusCopy[key] = "on";
      });
    setActiveFilters(filterListStatusCopy);
    setSelectAll(!isSelectAll);
  };

  const applyFilters = () => {
    setFilterText("");
    publishGlobalFilter(activeFilters);
    closePopover();
    if (OnApply) {
      if (SingleSelection) {
        OnApply(applyItem);
        setApplyItem({});
      }
      if (!SingleSelection) {
        OnApply(applyItems);
        setApplyItems([]);
      }
    }
  };

  const cancelFiltersSelection = () => {
    const cancelFiltersState: any = {};

    Object.entries(activeFilters).forEach(([key, value]) => {
      if (perfMonFilterObject[Field]?.includes(key)) {
        cancelFiltersState[key] = "on";
      }
      if (perfMonFilterObject[Field]?.includes(`${NEGATE_FILTER}${key}`)) {
        cancelFiltersState[key] = "off";
      }
    });
    setFilterText("");
    setActiveFilters(cancelFiltersState);
    if (isSelectAll) setSelectAll(false);
    closePopover();
  };

  const renderCustomOption = (option: any) => (
    <EuiFlexGroup justifyContent="spaceBetween" alignItems="center" gutterSize="s">
      <EuiFlexItem style={{ width: "220px", overflow: "hidden" }}>
        <span
          style={{
            alignItems: "center",
            display: "flex",
            justifyContent: "center",
            position: "relative",
          }}>
          <div
            className={`custom_checkbox ${
              activeFilters[option.name] === "on"
                ? "checkbox_checked"
                : activeFilters[option.name] === "off"
                  ? "checkbox_excluded"
                  : ""
            }`}></div>
        </span>
        <span style={{ paddingLeft: "25px" }}>{option.display ?? option.name}</span>
      </EuiFlexItem>

      <EuiFlexItem grow={false} style={{ width: "20px" }}>
        {option?.modificationRules?.canUpdate && (
          <IconButton
            size="xxs"
            type="pencil"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              OnEdit(option);
              cancelFiltersSelection();
            }}
          />
        )}
      </EuiFlexItem>
    </EuiFlexGroup>
  );

  const serachId = useGeneratedHtmlId();
  const dropdownOptions: any[] = visibleItems
    .filter((item: any) => {
      return (
        filterText.length === 0 ||
        (item.name + "").toLowerCase().includes(filterText.toLowerCase()) ||
        (item?.display ?? "").toLowerCase().includes(filterText.toLowerCase())
      );
    })
    .sort((a: any, b: any) => a.name - b.name)
    .map((i) => ({
      ...i,
      label: i.name,
    }));

  return (
    <>
      {
        <Popover
          noCloseOnScroll
          button={filterArrowDownButton}
          onClose={cancelFiltersSelection}
          isOpen={isPopoverOpen}
          ownFocus
          panelPaddingSize="none"
          children={
            <div style={{ borderRadius: "var(--box-radius)" }}>
              <EuiPopoverTitle className={classes.filterPopoverTitle}>
                <EuiFieldSearch
                  className={`${classes.search_input} filter-search-input`}
                  id={serachId}
                  isClearable
                  onChange={(e) => {
                    setFilterText(e.target.value);
                  }}
                  style={{
                    borderTopLeftRadius: 6,
                    borderTopRightRadius: 6,
                    borderBottom: SingleSelection ? "none" : ".6px solid var(--border-color)",
                  }}
                  value={filterText}
                />
                {!SingleSelection && dropdownOptions?.length > 0 && (
                  <div className="euiFilterSelect__items" style={{ margin: "5px" }}>
                    <EuiFlexGroup gutterSize="none">
                      <EuiFlexItem grow={false} style={{ paddingLeft: "8px" }}>
                        <EuiCheckbox
                          checked={isSelectAll}
                          id="select_all"
                          label={<span style={{ fontWeight: 400 }}>Select All</span>}
                          onChange={handleSelectAll}
                        />
                      </EuiFlexItem>
                      <EuiFlexItem style={{ paddingLeft: "8px" }}></EuiFlexItem>
                    </EuiFlexGroup>
                  </div>
                )}
              </EuiPopoverTitle>

              <div
                className="euiFilterSelect__items"
                style={visibleItems.length > 13 ? { height: "400px", scrollbarWidth: "thin" } : {}}>
                {visibleItems.length > 10 && (
                  <div>
                    <EuiSelectable
                      height={400}
                      isLoading={loading}
                      allowExclusions={AllowExclude}
                      singleSelection={SingleSelection}
                      listProps={{ showIcons: false }}
                      searchable={false}
                      renderOption={renderCustomOption}
                      options={dropdownOptions.filter((_, index) => index <= 300)}
                      onChange={(newOptions) => {
                        const selected = newOptions?.find?.((i) => i.checked === "on");
                        filterItemClick(selected);
                      }}
                      searchProps={{
                        placeholder: "",
                        compressed: false,
                        css: {
                          borderTopLeftRadius: 10,
                          borderTopRightRadius: 10,
                          borderBottom: ".6px solid var(--border-color)",
                          boxShadow: "none",
                          backgroundColor: "var(--card-background)",
                        },
                      }}
                      loadingMessage={"Loading filters"}
                      emptyMessage={"No filters found"}
                      noMatchesMessage={"No filters found"}>
                      {(list, search) => (
                        <div style={{ minWidth: 350, width: "100%" }}>
                          <EuiPopoverTitle paddingSize="s">{search}</EuiPopoverTitle>
                          {list}
                        </div>
                      )}
                    </EuiSelectable>
                  </div>
                )}

                {visibleItems.length <= 10 && (
                  <div>
                    <EuiSelectable
                      height={dropdownOptions?.length * 32}
                      isLoading={loading}
                      allowExclusions={AllowExclude}
                      singleSelection={SingleSelection}
                      listProps={{ showIcons: false }}
                      searchable={false}
                      renderOption={renderCustomOption}
                      options={dropdownOptions}
                      onChange={(newOptions) => {
                        const selected = newOptions?.find?.((i) => i.checked === "on");
                        filterItemClick(selected);
                      }}
                      searchProps={{
                        placeholder: "",
                        compressed: false,
                        css: {
                          borderTopLeftRadius: 10,
                          borderTopRightRadius: 10,
                          borderBottom: ".6px solid var(--border-color)",
                          boxShadow: "none",
                          backgroundColor: "var(--card-background)",
                        },
                      }}
                      loadingMessage={"Loading filters"}
                      emptyMessage={"No filters found"}
                      noMatchesMessage={"No filters found"}>
                      {(list, search) => (
                        <div style={{ minWidth: 350, width: "100%" }}>
                          <EuiPopoverTitle paddingSize="s">{search}</EuiPopoverTitle>
                          {list}
                        </div>
                      )}
                    </EuiSelectable>
                  </div>
                )}
              </div>

              <FDFilterDropdownFooter onApply={applyFilters} onCancel={cancelFiltersSelection} />
            </div>
          }
        />
      }
    </>
  );
};

export default FDFilterDropdownPerformance;
